/* eslint-disable no-unused-vars */
import { type Money, type Address, type PhoneNumberInput } from '../common/common'
import {
  type Occupation,
  type EnrollmentInvestmentExperienceCategory,
  type EnrollmentInvestmentObjectiveCategory,
  type EnrollmentTaxFilingStatusType,
  type PersonalNetWorthRange,
  type MaritalStatus,
} from '../customer/customer'
import { type TransactionMethod, type WireDirection, type TransactionLocation } from '../transactions/transactions'

export enum AchAccountType {
  SAVING = 'SAVING',
  CHECKING = 'CHECKING',
}

export enum LinkedAccountStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING = 'PENDING',
  VERIFYING = 'VERIFYING',
  CLOSED = 'CLOSED',
  FAILED = 'FAILED',
  FROZEN = 'FROZEN',
  UNKNOWN = 'UNKNOWN',
  EXPIRED = 'EXPIRED',
  EXPIRING_SOON = 'EXPIRING_SOON',
}

export interface LinkedAchAccount {
  __typename: 'LinkedAchAccount'
  achType: AchAccountType
  alias: number
  name: string
  mask: string
  linkedAccountStatus: LinkedAccountStatus
  institutionName: string
  identifier: string
}

export interface ExternalAccountBalance {
  balance: number // for in-app display
  available?: number // for MCP
  current?: number // for MCP if no available, guaranteed current
  limit?: number // for MCP
  currencyCode: string
  lastUpdated: string
}

export interface LinkedAccounts {
  achAccounts: LinkedAchAccount[]
  isSingleRestricted: boolean
}

export interface ExternalAccounts {
  institutions: LinkedExternalInstitution[]
  // accounts: LinkedExternalAccount[] // @deprecated
}

export interface LinkedExternalInstitution {
  __typename: 'LinkedExternalInstitution'
  institutionId: string
  institutionName: string
  linkedInstitutionStatus: LinkedInstitutionStatus
  accounts: LinkedExternalAccount[]
}

export enum LinkedInstitutionStatus {
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
  EXPIRING_SOON = 'EXPIRING_SOON',
  DEACTIVATED = 'DEACTIVATED',
}

export interface LinkedExternalAccount {
  __typename: 'LinkedExternalAccount'
  alias: number
  name: string
  // linkedAccountStatus: LinkedAccountStatus @deprecated
  // institutionName: string @deprecated
  externalBalance?: ExternalAccountBalance
}

export enum BankAccountType {
  HIGH_YIELD_CHECKING = 'HIGH_YIELD_CHECKING',
  HIGH_YIELD_SAVINGS = 'HIGH_YIELD_SAVINGS',
  LIQUID_TREASURIES = 'LIQUID_TREASURIES',
}

export enum BankAccountStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  UNKNOWN = 'UNKNOWN',
}

export interface BankAccountBalance {
  total: Money
  available: Money
}

export enum EgmCardStatus {
  ACTIVE = 'ACTIVE', // Status given to a card that has been activated and is ready for use
  PENDING_ACTIVATION = 'PENDING_ACTIVATION', // Status given to a new or reissued physical card prior to activation.
  SUSPENDED_BY_USER = 'SUSPENDED_BY_USER', // Status given to a card that prevents the card from being used but does not close it.
  SUSPENDED_BY_EGM = 'SUSPENDED_BY_EGM', // Status given to a card that prevents the card from being used but does not close it.
  LOST_STOLEN = 'LOST_STOLEN', // Status given to a card when the card has been reported lost or stolen + actioned by EGW Client Service.
  CLOSED = 'CLOSED', // Status given to a card when the card has been closed.
  EXPIRED = 'EXPIRED', // System-generated only status given to a card when the card has reached its expiration date.
  RETURNED_UNDELIVERABLE = 'RETURNED_UNDELIVERABLE', // Status given to a card that has been sent back to the return address or was never delivered to the cardholder.
  REPORTED_LOST_STOLEN = 'REPORTED_LOST_STOLEN', // Status given to a card when the card has been reported lost or stolen + Reported by Client
}

export enum CardOrderStatus {
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  ORDER_PENDING = 'ORDER_PENDING',
  ORDER_SUBMITTED = 'ORDER_SUBMITTED',
  ORDER_CONFIRMED_AND_COMPLETE = 'ORDER_CONFIRMED_AND_COMPLETE',
  ORDER_REJECTED = 'ORDER_REJECTED',
  ORDER_ONHOLD = 'ORDER_ONHOLD',
  ORDER_CANCELLED = 'ORDER_CANCELLED',
  ORDER_UNDELIVERABLE = 'ORDER_UNDELIVERABLE',
}

export interface BankAccountAttributes {
  minimumDeposit?: number
  minimumDepositMet?: boolean
}

export interface BankAccount {
  alias: number
  accountType: BankAccountType
  accountName: string
  routingNumber: string
  accountNumber: string
  attributes: BankAccountAttributes
  apy?: number
  status: BankAccountStatus
}

export interface CardOrderDetails {
  cardId: string
  cardStockId: string
  dateTime: string
  statusLastUpdatedDateTime?: string
  embossedLine1: string
  orderReason: string
  primaryAccountNumber: string
  shippingMethod: string
  collateralId?: string
  embossedLine2?: string
  expirationDate?: string
  formFactor?: string
  orderId?: string
  shippingAddress?: Address
  status?: CardOrderStatus
}

export interface CardDisplay {
  cardId: string
  cardName: string
  maskedPAN: string
  cardStatus: EgmCardStatus
  expDate: string
  needToSetPin: boolean
  formType?: string
  cardStockId?: string
  programId?: string
  cardCreated?: string
  cardLastStatusUpdated?: string
  embossedLine1?: string
  embossedLine2?: string
  accountId?: string
  profileId?: string
  cardStatusReason?: string
  merchantCredentialUpdate?: string
  pinBlock?: boolean
  pinSelection?: boolean
  orderDetails: CardOrderDetails[]
}

export enum EftType {
  CARD_PURCHASE = 'CARD_PURCHASE',
  CARD_PURCHASE_REFUND = 'CARD_PURCHASE_REFUND',
  P2P_OUT = 'P2P_OUT',
  OTHER = 'OTHER',
}

export enum BankActivityTypes {
  AchBankActivity = 'AchBankActivity',
  EftBankActivity = 'EftBankActivity',
  OtherBankActivity = 'OtherBankActivity',
  WireBankActivity = 'WireBankActivity',
}

export interface BankActivity {
  __typename: BankActivityTypes
  activityId: string
  displayName: string
  date: string
  amount: Money
  isProcessing: boolean
  method: TransactionMethod
  label: string
  note?: string
}

export interface BankAccountActivityInput {
  alias: number
}

export interface BankAccountActivityOutput {
  activity: BankActivity[]
}

export interface OtherBankActivity extends BankActivity {
  __typename: BankActivityTypes.OtherBankActivity
}

export interface AchBankActivity extends BankActivity {
  __typename: BankActivityTypes.AchBankActivity
  finInstOrMerchantName?: string
  routingNumber?: string
  accountName?: string
}

export interface EftBankActivity extends BankActivity {
  __typename: BankActivityTypes.EftBankActivity
  eftType: EftType
  acceptor?: string
  location?: TransactionLocation
}

export interface WireBankActivity extends BankActivity {
  __typename: BankActivityTypes.WireBankActivity
  direction: WireDirection
  institutionName: string
  imad?: string
}

export interface TransactionLimits {
  singleTransactionLimit?: number
  dailyLimit?: number
  monthlyLimit?: number
}

export interface AchLimits {
  __typename: 'AchLimits'
  credit: TransactionLimits
  debit: TransactionLimits
}

export enum AchLimitsErrorType {
  ACCOUNT_LIMIT_NOT_FOUND = 'ACCOUNT_LIMIT_NOT_FOUND',
}

export interface AchLimitsError {
  __typename: 'AchLimitsError'
  errorType: AchLimitsErrorType
}

export enum BankAccountTitle {
  SINGLE = 'SINGLE',
  JOINT_TENANTS_IN_COMMON = 'JOINT_TENANTS_IN_COMMON',
  JOINT_WITH_RIGHTS_OF_SURVIVORSHIP = 'JOINT_WITH_RIGHTS_OF_SURVIVORSHIP',
  JOINT_TENANTS_BY_THE_ENTIRETY = 'JOINT_TENANTS_BY_THE_ENTIRETY',
  JOINT_COMMUNITY_PROPERTY = 'JOINT_COMMUNITY_PROPERTY',
}
export interface BankAccountDetail {
  alias: number
  accountType: BankAccountType
  accountName: string
  routingNumber: string
  accountNumber: string
  apy?: number
  status: BankAccountStatus
  balance: BankAccountBalance
  debitCard: CardDisplay[]
  activity?: BankActivity[] /* @deprecated */
  achLimits: AchLimits | AchLimitsError
  attributes: BankAccountAttributes
  accountTitle: BankAccountTitle
}

export enum UpdateBankAccountNameErrorType {
  FAILED_TO_UPDATE_ACCOUNT_NAME = 'FAILED_TO_UPDATE_ACCOUNT_NAME',
}

export interface UpdateBankAccountNameError {
  __typename: 'UpdateBankAccountNameError'
  errorType: UpdateBankAccountNameErrorType
}

export interface UpdateBankAccountName {
  __typename: 'UpdateBankAccountName'
  isSuccessful: boolean
}

export type UpdateBankAccountNameOutput = UpdateBankAccountName | UpdateBankAccountNameError

export interface UpdateBankAccountNameInput {
  alias: number
  accountName: string
}

export interface UpdateAddressInput {
  address: Address
}

export interface UpdatePhoneInput {
  code?: string
  resendVerification?: boolean
  getStatus?: boolean
  phone?: PhoneNumberInput
}

export interface UpdateEmailInput {
  code?: string
  resendVerification?: boolean
  getStatus?: boolean
  email?: string
}

export enum UpdateProfileStatus {
  VERIFICATION_SENT = 'VERIFICATION_SENT',
  PENDING_VERIFICATION = 'PENDING_VERIFICATION',
  FAILED = 'FAILED',
  COMPLETE = 'COMPLETE',
}

export interface UpdateProfileSuccess {
  __typename: 'UpdateProfileSuccess'
  status: UpdateProfileStatus
}

export interface UpdateProfileError {
  __typename: 'UpdateProfileError'
  type: UpdateProfileErrorType
}

export enum UpdateProfileErrorType {
  UNABLE_TO_SEND_VERIFICATION = 'UNABLE_TO_SEND_VERIFICATION',
  VERIFICATION_FAILED = 'VERIFICATION_FAILED',
  VERIFICATION_EXPIRED = 'VERIFICATION_EXPIRED',
  UNABLE_TO_FIND_UPDATE_STATUS = 'UNABLE_TO_FIND_UPDATE_STATUS',
  VALIDATION_FAILED = 'VALIDATION_FAILED',
}

export type UpdateProfileOutput = UpdateProfileSuccess | UpdateProfileError

enum EmploymentStatus {
  UNEMPLOYED = 'UNEMPLOYED',
  STUDENT = 'STUDENT',
  RETIRED = 'RETIRED',
  INDEPENDENT = 'INDEPENDENT',
  EMPLOYED = 'EMPLOYED',
}

export interface UpdateEmploymentInfoInput {
  employmentStatus?: EmploymentStatus
  occupation?: Occupation
}

export interface UpdateFinancialInfoInput {
  personalAnnualGrossIncome?: number
  personalNetWorthRange?: PersonalNetWorthRange
  maritalStatus?: MaritalStatus
}

export interface UpdateSuitabilityInput {
  isBrokerDealer?: boolean
  brokerDealerEmployer?: string
  isDirectorPolicyMakerOfCompany?: boolean
  directorPolicyMakerOfCompany?: string
  taxFilingStatus?: EnrollmentTaxFilingStatusType
  investmentObjective?: EnrollmentInvestmentObjectiveCategory
  investmentExperience?: EnrollmentInvestmentExperienceCategory
  isPoliticallyExposedPerson?: boolean
  affiliatedPoliticalOrganizations?: string[]
  hasPoliticallyExposedImmediateFamilyMembers?: boolean
  politicallyExposedImmediateFamilyMembers?: string[]
}

export interface CardStatusUpdateInput {
  cardId: string
}

export type CardStatusUpdateOutput = CardStatusUpdateSuccess | CardStatusUpdateError

export interface CardStatusUpdateSuccess {
  __typename: 'CardStatusUpdateSuccess'
  status: EgmCardStatus
}
enum CardStatusUpdateErrorType {
  UNAUTHORIZED_USER_CARD,
  UNAUTHORIZED_USER_CARD_ACTION,
  CARD_STATUS_UPDATE_NOT_ALLOWED,
  CARD_ACTION_FAILED,
  FEATURE_NOT_ENABLED,
}

export interface CardStatusUpdateError {
  __typename: 'CardStatusUpdateError'
  errorType: CardStatusUpdateErrorType
}

export interface ActivateCardInput {
  cardId: string
  expirationDate: string
  last4PAN: string
}
export interface EditLinkedAchAccountNameInput {
  alias: number
  accountName: string
}

export enum PatchAchAccountErrorType {
  ACCOUNT_NOT_FOUND = 'ACCOUNT_NOT_FOUND',
  ACCOUNT_NAME_LENGTH_EXCEEDS_LIMIT = 'ACCOUNT_NAME_LENGTH_EXCEEDS_LIMIT',
}

export interface PatchAchAccountError {
  __typename: 'PatchAchAccountError'
  errorType?: PatchAchAccountErrorType
}

export type EditLinkedAchAccountNameOutput = LinkedAchAccount | PatchAchAccountError

export interface UnlinkAchAccountInput {
  alias: number
}

export interface UnlinkAchAccountOutput {
  isSuccessful: boolean
}

export interface CreatePlaidLinkTokenV2Input {
  alias?: number
}

export interface PlaidLinkToken {
  __typename: 'PlaidLinkToken'
  linkToken: string
  expiration: string
}

export enum CreatePlaidLinkTokenV2ErrorType {
  UNABLE_TO_CREATE_LINK_TOKEN = 'UNABLE_TO_CREATE_LINK_TOKEN',
}

export interface CreatePlaidLinkTokenV2Error {
  __typename: 'CreatePlaidLinkTokenV2Error'
  errorType: CreatePlaidLinkTokenV2ErrorType
}

export enum PlaidLinkType {
  ACH = 'ACH',
  BANKING = 'BANKING',
  INVESTMENTS = 'INVESTMENTS',
}

export interface CreatePlaidMultiLinkTokenInput {
  linkType: PlaidLinkType
}

export type CreatePlaidLinkTokenV2Output = PlaidLinkToken | CreatePlaidLinkTokenV2Error

export interface InitiateRelinkExternalAccountInput {
  institutionId: string
}

export interface RelinkExternalAccountError {
  __typename: 'RelinkExternalAccountError'
  errorType: RelinkExternalAccountErrorType
}

enum RelinkExternalAccountErrorType {
  ACCOUNT_NOT_FOUND = 'ACCOUNT_NOT_FOUND',
  PLAID_LINK_ERROR = 'PLAID_LINK_ERROR',
  INVALID_STATUS = 'INVALID_STATUS',
}

export type InitiateRelinkExternalAccountOutput = PlaidLinkToken | RelinkExternalAccountError

export interface CompleteRelinkExternalAccountInput {
  linkToken: string
  institutionId: string
}

export interface CompleteRelinkExternalAccountOutput {
  __typename: 'CompleteRelinkExternalAccountOutput'
  isSuccessful: boolean
}

export type CompleteRelinkExternalAccountResult = CompleteRelinkExternalAccountOutput | RelinkExternalAccountError

export interface UnlinkExternalAccountInput {
  alias: number
}

export interface UnlinkExternalAccountOutput {
  __typename: 'UnlinkExternalAccountOutput'
  isSuccessful: boolean
}

export interface UnlinkExternalAccountError {
  __typename: 'UnlinkExternalAccountError'
  errorType: UnlinkExternalAccountErrorType
}

export enum UnlinkExternalAccountErrorType {
  ACCOUNT_NOT_FOUND = 'ACCOUNT_NOT_FOUND',
  UNAUTHORIZED_ACCOUNT_ACTION = 'UNAUTHORIZED_ACCOUNT_ACTION',
}

export type UnlinkExternalAccountResult = UnlinkExternalAccountOutput | UnlinkExternalAccountError

export interface PlaidLinkMetadataAccountInput {
  accountId: string
  name: string
  mask?: string
  type: string
  subtype?: string
}

export interface PlaidLinkMetadataInput {
  institutionId: string
  institutionName: string
  accounts: PlaidLinkMetadataAccountInput[]
}

export interface LinkPlaidAchAccountInput {
  publicToken: string
  accountName?: string
  metadata: PlaidLinkMetadataInput
}

export enum PlaidLinkAchErrorType {
  INVALID_PUBLIC_TOKEN,
  INVALID_AMOUNT_LINKED_ACCOUNT,
  PLAID_INSTITUTION_ALREADY_LINKED,
  INVALID_PLAID_LINK_ACCOUNT_TYPE,
  USER_SINGLE_LINKED_ACCOUNT_RESTRICTED,
  UNABLE_TO_LINK_PLAID,
}

export interface LinkPlaidAchAccountError {
  __typename: 'LinkPlaidAchAccountError'
  errorType: PlaidLinkAchErrorType
}

export type LinkPlaidAchAccountOutput = LinkedAchAccount | LinkPlaidAchAccountError

export interface LinkAchAccountInput {
  accountName: string
  accountNumber: string
  routingNumber: string
  achType: AchAccountType
}

export interface LinkAchAccountSuccess {
  __typename: 'LinkAchAccountSuccess'
  alias: number
  displayName: string
  mask: string
  linkedAccountStatus: LinkedAccountStatus
}

export interface LinkAchAccountError {
  __typename: 'LinkAchAccountError'
  errorType?: LinkAchAccountErrorType
}

export enum LinkAchAccountErrorType {
  UNABLE_TO_LOOKUP_ROUTING_NUMBER = 'UNABLE_TO_LOOKUP_ROUTING_NUMBER',
  ACCOUNT_ALREADY_LINKED = 'ACCOUNT_ALREADY_LINKED',
  USER_SINGLE_LINKED_ACCOUNT_RESTRICTED = 'USER_SINGLE_LINKED_ACCOUNT_RESTRICTED',
}

export type LinkAchAccountOutput = LinkAchAccountSuccess | LinkAchAccountError

export interface UnlinkExternalAccountsByInstitutionInput {
  institutionId: string
}

export type UnlinkExternalAccountsByInstitutionResult =
  | UnlinkExternalAccountsByInstitutionOutput
  | UnlinkExternalAccountsByInstitutionError

export interface UnlinkExternalAccountsByInstitutionOutput {
  __typename: 'UnlinkExternalAccountsByInstitutionOutput'
  isSuccessful: boolean
}

export interface UnlinkExternalAccountsByInstitutionError {
  __typename: 'UnlinkExternalAccountsByInstitutionError'
  errorType: UnlinkExternalAccountsByInstitutionErrorType
}

export enum UnlinkExternalAccountsByInstitutionErrorType {
  PLAID_ITEM_NOT_FOUND = 'PLAID_ITEM_NOT_FOUND',
  PLAID_ITEM_REMOVAL_FAILED = 'PLAID_ITEM_REMOVAL_FAILED',
}

export interface InitiateUpdatePlaidExternalAccountInput {
  institutionId: string
}

export type InitiateUpdatePlaidExternalAccountResult = PlaidLinkToken | UpdatePlaidExternalAccountError

export interface UpdatePlaidExternalAccountError {
  __typename: 'UpdatePlaidExternalAccountError'
  errorType: UpdatePlaidExternalAccountErrorType
}

enum UpdatePlaidExternalAccountErrorType {
  PLAID_ITEM_NOT_FOUND = 'PLAID_ITEM_NOT_FOUND',
  PLAID_LINK_ERROR = 'PLAID_LINK_ERROR',
}

export interface CompleteUpdatePlaidExternalAccountInput {
  institutionId: string
  linkToken: string
  accounts: PlaidLinkMetadataAccountInput[]
}

export interface CompleteUpdatePlaidExternalAccountSuccess {
  __typename: 'CompleteUpdatePlaidExternalAccountSuccess'
  isSuccessful: boolean
}

export type CompleteUpdatePlaidExternalAccountResult =
  | CompleteUpdatePlaidExternalAccountSuccess
  | UpdatePlaidExternalAccountError
